import { apiService } from './apiService';
import {
  DataRoom,
  DataRoomFolder,
  DataRoomDocument,
  DataRoomShareLink,
  DataRoomStats,
  DataRoomVisitor,
  CreateDataRoomInput,
  UpdateDataRoomInput,
  CreateFolderInput,
  CreateShareLinkInput,
} from '../types/dataRoom';

const BASE_URL = '/api/data-rooms';

export const dataRoomService = {
  // ============================================
  // DATA ROOMS
  // ============================================

  /**
   * Get all data rooms for the current user
   */
  async getDataRooms(): Promise<DataRoom[]> {
    const response = await apiService.get(`${BASE_URL}`);
    return response.data.data;
  },

  /**
   * Get a single data room by ID (includes folders and documents)
   */
  async getDataRoom(id: string): Promise<DataRoom> {
    const response = await apiService.get(`${BASE_URL}/${id}`);
    return response.data.data;
  },

  /**
   * Create a new data room
   */
  async createDataRoom(input: CreateDataRoomInput): Promise<DataRoom> {
    const response = await apiService.post(`${BASE_URL}`, input);
    return response.data.data;
  },

  /**
   * Update an existing data room
   */
  async updateDataRoom(id: string, input: UpdateDataRoomInput): Promise<DataRoom> {
    const response = await apiService.put(`${BASE_URL}/${id}`, input);
    return response.data.data;
  },

  /**
   * Delete a data room
   */
  async deleteDataRoom(id: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Publish a data room (set status to 'active')
   */
  async publishDataRoom(id: string): Promise<DataRoom> {
    const response = await apiService.post(`${BASE_URL}/${id}/publish`);
    return response.data.data;
  },

  /**
   * Unpublish a data room (set status to 'draft')
   */
  async unpublishDataRoom(id: string): Promise<DataRoom> {
    const response = await apiService.post(`${BASE_URL}/${id}/unpublish`);
    return response.data.data;
  },

  // ============================================
  // FOLDERS
  // ============================================

  /**
   * Get all folders for a data room
   */
  async getFolders(dataRoomId: string): Promise<DataRoomFolder[]> {
    const response = await apiService.get(`${BASE_URL}/${dataRoomId}/folders`);
    return response.data.data;
  },

  /**
   * Create a new folder in a data room
   */
  async createFolder(dataRoomId: string, input: CreateFolderInput): Promise<DataRoomFolder> {
    const response = await apiService.post(`${BASE_URL}/${dataRoomId}/folders`, input);
    return response.data.data;
  },

  /**
   * Update a folder name
   */
  async updateFolder(dataRoomId: string, folderId: string, name: string): Promise<DataRoomFolder> {
    const response = await apiService.put(`${BASE_URL}/${dataRoomId}/folders/${folderId}`, { name });
    return response.data.data;
  },

  /**
   * Delete a folder
   */
  async deleteFolder(dataRoomId: string, folderId: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/${dataRoomId}/folders/${folderId}`);
  },

  // ============================================
  // DOCUMENTS
  // ============================================

  /**
   * Upload a document to a folder
   */
  async uploadDocument(dataRoomId: string, folderId: string, file: File): Promise<DataRoomDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_id', folderId);

    try {
      console.log('[DataRoomService] Starting upload:', {
        dataRoomId,
        folderId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      // Don't set Content-Type header - let browser set it automatically with correct boundary
      const response = await apiService.post(`${BASE_URL}/${dataRoomId}/documents`, formData);
      console.log('[DataRoomService] Upload successful');
      return response.data.data;
    } catch (error: any) {
      console.error('[DataRoomService] Upload failed:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        code: error?.code,
      });
      throw error;
    }
  },

  /**
   * Delete a document
   */
  async deleteDocument(dataRoomId: string, docId: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/${dataRoomId}/documents/${docId}`);
  },

  /**
   * Owner-side: stream document bytes for in-app viewing.
   * Returns an ArrayBuffer suitable for PDF.js / Blob construction.
   * Bytes never become a publicly-fetchable URL.
   */
  async fetchOwnerDocumentBytes(docId: string): Promise<ArrayBuffer> {
    const response = await apiService.get(`${BASE_URL}/documents/${docId}/stream`, {
      responseType: 'arraybuffer',
    });
    return response.data as ArrayBuffer;
  },

  /**
   * Owner-side: build the Microsoft Office Online viewer URL for an
   * Office document. The src embedded in the URL is a 60-second signed
   * URL — short enough that copying it out of the iframe is useless.
   */
  async getOwnerOfficeViewerUrl(docId: string): Promise<string> {
    const response = await apiService.get(`${BASE_URL}/documents/${docId}/office-viewer-url`);
    return response.data.data.viewerUrl;
  },

  /**
   * Public-side: stream document bytes via share token URL.
   * Auth is handled implicitly by the token in the path.
   */
  async fetchPublicDocumentBytesByToken(token: string, docId: string): Promise<ArrayBuffer> {
    const response = await apiService.get(
      `/api/public/data-room/${encodeURIComponent(token)}/documents/${docId}/stream`,
      { responseType: 'arraybuffer' }
    );
    return response.data as ArrayBuffer;
  },

  async fetchPublicDocumentBytesBySlug(slug: string, docId: string): Promise<ArrayBuffer> {
    const response = await apiService.get(
      `/api/public/data-room/slug/${encodeURIComponent(slug)}/documents/${docId}/stream`,
      { responseType: 'arraybuffer' }
    );
    return response.data as ArrayBuffer;
  },

  async getPublicOfficeViewerUrlByToken(token: string, docId: string): Promise<string> {
    const response = await apiService.get(
      `/api/public/data-room/${encodeURIComponent(token)}/documents/${docId}/office-viewer-url`
    );
    return response.data.data.viewerUrl;
  },

  async getPublicOfficeViewerUrlBySlug(slug: string, docId: string): Promise<string> {
    const response = await apiService.get(
      `/api/public/data-room/slug/${encodeURIComponent(slug)}/documents/${docId}/office-viewer-url`
    );
    return response.data.data.viewerUrl;
  },

  // ============================================
  // SHARE LINKS
  // ============================================

  /**
   * Get all share links for a data room
   */
  async getShareLinks(dataRoomId: string): Promise<DataRoomShareLink[]> {
    const response = await apiService.get(`${BASE_URL}/${dataRoomId}/share-links`);
    return response.data.data;
  },

  /**
   * Create a new share link
   */
  async createShareLink(dataRoomId: string, input?: CreateShareLinkInput): Promise<DataRoomShareLink> {
    const response = await apiService.post(`${BASE_URL}/${dataRoomId}/share-links`, input || {});
    return response.data.data;
  },

  /**
   * Update a share link (activate/deactivate)
   */
  async updateShareLink(dataRoomId: string, linkId: string, isActive: boolean): Promise<DataRoomShareLink> {
    const response = await apiService.put(`${BASE_URL}/${dataRoomId}/share-links/${linkId}`, {
      is_active: isActive,
    });
    return response.data.data;
  },

  /**
   * Delete a share link
   */
  async deleteShareLink(dataRoomId: string, linkId: string): Promise<void> {
    await apiService.delete(`${BASE_URL}/${dataRoomId}/share-links/${linkId}`);
  },

  // ============================================
  // ANALYTICS
  // ============================================

  /**
   * Get analytics stats for a data room
   */
  async getStats(dataRoomId: string): Promise<DataRoomStats> {
    const response = await apiService.get(`${BASE_URL}/${dataRoomId}/analytics`);
    return response.data.data;
  },

  /**
   * Get visitors for a data room
   */
  async getVisitors(dataRoomId: string): Promise<DataRoomVisitor[]> {
    const response = await apiService.get(`${BASE_URL}/${dataRoomId}/visitors`);
    return response.data.data;
  },
  // ============================================
  // PUBLIC VIEWING (No Auth Required)
  // ============================================

  async getPublicDataRoom(token: string): Promise<any> {
    const response = await apiService.get(`/api/public/data-room/${token}`);
    return response.data.data;
  },

  async recordPublicView(token: string, input?: { viewer_email?: string }): Promise<void> {
    await apiService.post(`/api/public/data-room/${token}/view`, input || {});
  },

  // ============================================
  // PUBLIC VIEWING — SLUG-BASED (New URL Format)
  // ============================================

  async getPublicDataRoomBySlug(linkSlug: string): Promise<any> {
    const response = await apiService.get(`/api/public/data-room/slug/${linkSlug}`);
    return response.data.data;
  },

  async recordPublicViewBySlug(linkSlug: string, input?: { viewer_email?: string }): Promise<void> {
    await apiService.post(`/api/public/data-room/slug/${linkSlug}/view`, input || {});
  },
};

export default dataRoomService;
